import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { cn } from '@localagent/ui/lib/utils';
import type { GoalActivityInitiative, WorkOrdering } from '@localagent/sdk';
import { ProjectCard } from '../../components/work/ProjectCard';
import {
  assigneeResolver,
  blockerIndex,
  nodeLabelIndex,
} from '../../lib/project-board-helpers';
import {
  CANNOT_ORDER_DETAIL,
  CANNOT_ORDER_HEADLINE,
  NO_ORDERING_DETAIL,
  NO_ORDERING_HEADLINE,
  criticalPathSummary,
  initiativeEdges,
  initiativeMilestone,
  rankRoadmap,
  stageLabel,
} from '../../lib/roadmap-helpers';
import { useGoalActivity, useTeams, useWorkOrderings } from '../../hooks/useWorkManagement';
import { usePublishViewSnapshot } from '../../components/shogun/usePublishViewSnapshot';
import { initiativeRecords, scopeRecord } from '../../lib/view-snapshot-helpers';
import { usePositions } from '../../hooks/useOrgChart';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import type { OrgPosition } from '../../types/orgchart';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

interface LaneProps {
  initiative: GoalActivityInitiative;
  stage: number | null;
  onCriticalPath: boolean;
  hrefFor: (projectId: string) => string;
  resolvePosition: (id?: string | null) => string;
  blockersFor: (nodeId: string) => string[] | undefined;
}

function InitiativeLane({
  initiative,
  stage,
  onCriticalPath,
  hrefFor,
  resolvePosition,
  blockersFor,
}: LaneProps) {
  const milestone = initiativeMilestone(initiative);
  const laneBlockers = blockersFor(initiative.id);

  return (
    <div
      data-testid={`roadmap-lane-${initiative.id}`}
      data-stage={stage === null ? 'none' : String(stage)}
      data-critical={onCriticalPath ? 'true' : 'false'}
      data-project-count={initiative.projects.length}
      className={cn(
        'rounded-lg bg-card p-3',
        onCriticalPath && 'bg-primary/10',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="truncate text-sm font-semibold" title={initiative.name}>
          {initiative.name}
        </h4>
        <span
          data-testid={`roadmap-milestone-${initiative.id}`}
          data-filled={milestone.filled ? 'true' : 'false'}
          data-accepted={milestone.accepted}
          data-total={milestone.total}
          title={
            // Copy names what it derives, never a milestone
            // (work_management ADR-031).
            milestone.filled
              ? 'Every project accepted'
              : `Projects accepted: ${milestone.accepted} of ${milestone.total}`
          }
          className={cn(
            'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
            milestone.filled
              ? 'bg-green-600 text-white'
              : 'bg-muted text-muted-foreground',
          )}
        >
          <span aria-hidden="true">{milestone.filled ? '◆' : '◇'}</span>
          {milestone.accepted}/{milestone.total} accepted
        </span>
      </div>

      {onCriticalPath && (
        <p
          data-testid={`roadmap-lane-critical-${initiative.id}`}
          className="mt-1 text-[10px] font-medium text-primary"
        >
          On the critical path
        </p>
      )}

      {laneBlockers && (
        <p
          data-testid={`roadmap-lane-blockers-${initiative.id}`}
          className="mt-1 text-[10px] text-muted-foreground"
        >
          Waits for {laneBlockers.join(', ')}
        </p>
      )}

      <div className="mt-2 space-y-2">
        {initiative.projects.length === 0 ? (
          <p
            data-testid={`roadmap-lane-empty-${initiative.id}`}
            className="text-[10px] text-muted-foreground"
          >
            No projects yet
          </p>
        ) : (
          initiative.projects.map((project) => (
            <ProjectCard
              key={project.id}
              compact
              project={project}
              to={hrefFor(project.id)}
              assigneeTitle={resolvePosition(project.assignee_position_id)}
              blockers={blockersFor(project.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface RoadmapViewProps {
  initiatives: GoalActivityInitiative[];
  orderings: WorkOrdering[];
  positions: OrgPosition[] | undefined;
  hrefFor: (projectId: string) => string;
}

export function InitiativeRoadmapView({
  initiatives,
  orderings,
  positions,
  hrefFor,
}: RoadmapViewProps) {
  const edges = useMemo(() => initiativeEdges(orderings), [orderings]);
  const ranking = useMemo(
    () => rankRoadmap(initiatives.map((initiative) => initiative.id), edges),
    [initiatives, edges],
  );
  const byId = useMemo(
    () => new Map(initiatives.map((initiative) => [initiative.id, initiative])),
    [initiatives],
  );
  const resolve = useMemo(() => assigneeResolver(positions), [positions]);
  const blockers = useMemo(
    () => blockerIndex(orderings, nodeLabelIndex(initiatives)),
    [orderings, initiatives],
  );
  const critical = useMemo(() => new Set(ranking.criticalPath), [ranking]);

  const laneFor = (id: string, stage: number | null) => {
    const initiative = byId.get(id);
    if (!initiative) throw new Error(`ranked node ${id} is not one of the goal's initiatives`);
    return (
      <InitiativeLane
        key={id}
        initiative={initiative}
        stage={stage}
        onCriticalPath={critical.has(id)}
        hrefFor={hrefFor}
        resolvePosition={resolve.position}
        blockersFor={(nodeId) => blockers.get(nodeId)}
      />
    );
  };

  if (initiatives.length === 0) {
    return (
      <div data-testid="roadmap-no-initiatives" className="text-sm text-muted-foreground">
        This goal has no initiatives yet.
      </div>
    );
  }

  if (edges.length === 0) {
    return (
      <div className="space-y-4">
        <div
          data-testid="roadmap-no-orderings"
          className="rounded-md bg-muted p-4"
        >
          <p className="text-sm font-medium">{NO_ORDERING_HEADLINE}</p>
          <p className="mt-1 text-xs text-muted-foreground">{NO_ORDERING_DETAIL}</p>
        </div>
        <div
          data-testid="roadmap-unranked"
          data-count={initiatives.length}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {initiatives.map((initiative) => laneFor(initiative.id, null))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p
        data-testid="roadmap-critical-path"
        data-length={ranking.criticalPath.length}
        className="text-xs text-muted-foreground"
      >
        Critical path: {criticalPathSummary(ranking.criticalPath, ranking.rankOf)}
      </p>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {ranking.stages.map((ids, index) => (
          <div
            key={stageLabel(index)}
            data-testid={`roadmap-stage-${index}`}
            data-count={ids.length}
            className="w-72 flex-shrink-0 space-y-2"
          >
            <h3 className="text-sm font-semibold">{stageLabel(index)}</h3>
            {ids.map((id) => laneFor(id, index))}
          </div>
        ))}

        {ranking.residual.length > 0 && (
          <div
            data-testid="roadmap-residual"
            data-count={ranking.residual.length}
            className="w-72 flex-shrink-0 space-y-2 rounded-lg bg-destructive/10 p-2"
          >
            <h3 className="text-sm font-semibold text-destructive">{CANNOT_ORDER_HEADLINE}</h3>
            <p className="text-[10px] text-muted-foreground">{CANNOT_ORDER_DETAIL}</p>
            {ranking.residual.map((id) => laneFor(id, null))}
          </div>
        )}
      </div>
    </div>
  );
}

export function InitiativeRoadmap() {
  const { teamId, goalId } = useParams<{ teamId: string; goalId: string }>();
  const { data: activity, error: activityError } = useGoalActivity(goalId ?? null);
  const { data: orderings, error: orderingsError } = useWorkOrderings(goalId ?? null);
  const { data: positions } = usePositions();
  const { data: teams } = useTeams();
  const { pathname } = useLocation();

  const snapshot = useMemo(() => {
    if (!activity || !teamId || !goalId) return null;
    const ranking = rankRoadmap(
      activity.initiatives.map((initiative) => initiative.id),
      initiativeEdges(orderings),
    );
    const stageOf = new Map<string, string>();
    ranking.stages.forEach((stage, index) => {
      for (const id of stage) stageOf.set(id, stageLabel(index));
    });
    const { counts, visible } = initiativeRecords(
      activity.initiatives,
      (id) => stageOf.get(id) ?? NO_ORDERING_HEADLINE,
    );
    return {
      route: pathname,
      context: {
        surface: 'work',
        primary_type: 'initiative',
        scope: [
          scopeRecord('team', teamId, teams?.find((t) => t.id === teamId)?.name ?? 'Team'),
          scopeRecord('goal', goalId, activity.goal.objective),
        ],
        counts,
        visible,
      },
    };
  }, [activity, orderings, teamId, goalId, teams, pathname]);
  usePublishViewSnapshot(snapshot);

  if (!teamId || !goalId) {
    throw new Error('InitiativeRoadmap requires :teamId and :goalId route params');
  }
  const goalPath = `/work/teams/${teamId}/goals/${goalId}`;

  const readError =
    errorWithNothingCachedToShow(activity, activityError) ??
    errorWithNothingCachedToShow(orderings, orderingsError);

  return (
    <div className="flex h-full flex-col" data-testid="initiative-roadmap">
      <div className="flex items-center justify-between gap-4 border-b px-6 py-3">
        <div className="min-w-0">
          {activity && (
            <>
              <h2 className="truncate text-sm font-semibold" title={activity.goal.objective}>
                {activity.goal.objective}
              </h2>
              <p className="text-xs text-muted-foreground" data-testid="roadmap-initiative-count">
                {activity.initiatives.length} initiatives
              </p>
            </>
          )}
        </div>
        <Link
          to={`${goalPath}/projects`}
          className="text-xs text-primary hover:underline"
          data-testid="roadmap-projects-link"
        >
          Projects
        </Link>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {readError ? (
          <p className="text-sm text-destructive" data-testid="roadmap-error">
            Failed to load the roadmap: {readError.message}
          </p>
        ) : activity === undefined || orderings === undefined ? (
          <LoadingState data-testid="roadmap-loading" label="Loading roadmap..." />
        ) : (
          <InitiativeRoadmapView
            initiatives={activity.initiatives}
            orderings={orderings}
            positions={positions}
            hrefFor={(projectId) => `${goalPath}/projects/${projectId}`}
          />
        )}
      </div>
    </div>
  );
}
